// AddressField.tsx
import React, { useEffect, useState, useRef } from "react";
import TextInput from "../TextInput/TextInput";
import GooglePlaceSearchComponent from "../GooglePlaceSearchComponent/GooglePlaceSearchComponent";
import { ProvinceList, CITY_KEY, COUNTRY_KEY, STATE_KEY, STREET_KEY, STREET_NUMBER_KEY, UNIT, ZIP_KEY } from "../../utils/constants";
import { formatPostalCode } from "../../utils";
import SelectNew from "../Select/SelectNew";

export interface GoogleAddressComponent {
  long_name: string;
  short_name: string;
  types: string[];
}

export interface AddressFieldValue {
  value: string;
  isValid: boolean;
  onModeChange?: (isManual: boolean) => void;
  components: GoogleAddressComponent[];
}

export interface AddressFieldProps {
  label: string;
  required?: boolean;
  initialValue?: string;
  initialComponents?: GoogleAddressComponent[];
  onChange: (newVal: AddressFieldValue) => void;
  onModeChange?: (isManual: boolean) => void;
  hideGooglePlaceSearch: boolean;
  disabled?: boolean;
  error?: boolean;
  errorMsg?: string;
  initialData?: any;
  addressType: string;
  darkBg: boolean;
  territoryValue?: string;
  resetOnToggle?: boolean;
}

export interface OfficeData {
  addressLine1?: string; // renamed from streetNumber
  addressLine2?: string;
  unit?: any;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  formattedAddress?: string;
}

const AddressField: React.FC<AddressFieldProps> = ({
  label,
  required = false,
  initialValue = "",
  initialComponents = [],
  onChange,
  disabled = false,
  error,
  errorMsg,
  initialData,
  onModeChange,
  hideGooglePlaceSearch,
  addressType,
  darkBg,
  territoryValue,
  resetOnToggle,
}) => {
  const [useManual, setUseManual] = useState(hideGooglePlaceSearch);
  // Store the current Google address when switching modes
  const [storedGoogleAddress, setStoredGoogleAddress] = useState<{ value: string; components: GoogleAddressComponent[] } | null>(
    initialValue && initialComponents.length > 0 ? { value: initialValue, components: initialComponents } : null
  );
  // Track if we're in the middle of a toggle to prevent infinite loops
  const isTogglingRef = useRef(false);
  // Track the last initialData to prevent unnecessary updates
  const lastInitialDataRef = useRef(initialData);
  // Track if user has manually edited the fields to prevent overwriting their changes
  const hasManualEditsRef = useRef(false);

  // State for suite/unit field in Google mode
  const [googleModeSuite, setGoogleModeSuite] = useState(() => {
    // Initialize from initialData if available (for editing existing addresses)
    if (addressType === "office" && initialData?.street1) {
      return initialData.street1;
    } else if (addressType === "billing" && initialData?.billingStreet1) {
      return initialData.billingStreet1;
    }
    return "";
  });

  // Helper to deep compare initialData objects
  const isInitialDataEqual = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;
    return (
      a.street === b.street &&
      a.street1 === b.street1 &&
      a.street2 === b.street2 &&
      a.city === b.city &&
      a.state === b.state &&
      a.zip === b.zip &&
      a.country === b.country
    );
  };
  useEffect(() => {
    if (!useManual || !territoryValue) return;
    setCustom(prev => {
      const next = { ...prev, state: territoryValue };
      buildAndNotify(next);
      return next;
    });
  }, [territoryValue, useManual]);

  // Helper function to extract address fields from Google components
  const extractAddressFromComponents = (components: GoogleAddressComponent[]): Partial<Required<OfficeData>> => {
    const getValue = (key: string | string[]) => {
      if (Array.isArray(key)) {
        for (const k of key) {
          const comp = components.find(c => c.types.includes(k));
          if (comp) return comp.long_name || comp.short_name;
        }
        return "";
      } else {
        const comp = components.find(c => c.types.includes(key));
        return comp ? comp.long_name || comp.short_name : "";
      }
    };

    const streetNumber = getValue(STREET_NUMBER_KEY);
    const route = getValue(STREET_KEY);
    const unit = getValue(UNIT);
    const city = getValue(CITY_KEY);
    const state = getValue(STATE_KEY);
    const zip = getValue(ZIP_KEY);
    const country = getValue(COUNTRY_KEY) || "CA";

    // Combine street_number + route for addressLine1 (e.g., "123 Main Street")
    // Use subpremise (unit) for addressLine2 - prepend "Suite " if subpremise is a simple unit number
    const addressLine1 = [streetNumber, route].filter(Boolean).join(" ").trim();
    // Only prepend "Suite " if the unit is a simple value (no spaces, doesn't already start with common prefixes)
    const shouldPrependSuite = unit && !unit.includes(" ") && !/^(suite|floor|unit|apt|apartment|ste|fl)/i.test(unit);
    const addressLine2 = unit ? (shouldPrependSuite ? `Suite ${unit}` : unit) : "";

    return {
      addressLine1,
      addressLine2,
      unit: "",
      city: city || "",
      state: state || "",
      zip: formatPostalCode(zip) || "",
      country: country || "CA",
      formattedAddress: "",
    };
  };

  const buildInitialCustom = (addressType: string, components?: GoogleAddressComponent[]): Required<OfficeData> => {
    // If we have Google address components, use them to populate manual fields
    if (components && components.length > 0) {
      const extracted = extractAddressFromComponents(components);
      return {
        addressLine1: extracted.addressLine1 || "",
        addressLine2: extracted.addressLine2 || "",
        unit: "",
        city: extracted.city || "",
        state: extracted.state || "",
        zip: extracted.zip || "",
        country: extracted.country || "CA",
        formattedAddress: extracted.formattedAddress || "",
      };
    }
    if (addressType === "office") {
      return {
        addressLine1: initialData?.street || "",
        addressLine2: initialData?.street1 || "",
        unit: "",
        city: initialData?.city || "",
        state: initialData?.state || "",
        zip: initialData?.zip || "",
        country: initialData?.country || " ",
        formattedAddress: initialData?.formattedAddress || "",
      };
    } else if (addressType === "billing") {
      return {
        addressLine1: initialData?.billingStreet || "",
        addressLine2: initialData?.billingStreet1 || "",
        unit: "",
        city: initialData?.billingCity || "",
        state: initialData?.billingState || "",
        zip: initialData?.billingZip || "",
        country: "CA",
        formattedAddress: initialData?.billingFormattedAddress || "",
      };
    }
    return {
      addressLine1: "",
      addressLine2: "",
      unit: "",
      city: "",
      state: "",
      zip: "",
      country: "CA",
      formattedAddress: "",
    };
  };

  const [custom, setCustom] = useState(() => buildInitialCustom(addressType));

  const buildAndNotify = (fields: typeof custom) => {
    const parts = [fields.addressLine1, fields.addressLine2, fields.city, fields.state, fields.zip, fields.country];
    const value = parts.filter(Boolean).join(", ");

    const isValid = [fields.addressLine1, fields.city, fields.zip, fields.country].every(v => !!v);

    // Map UI fields to Google component types for consistent handling:
    // - addressLine1 (full street address) → street_number (will be saved as 'street' in DB)
    // - addressLine2 (unit/suite) → subpremise (will be saved as 'street_1' in DB)
    const components: GoogleAddressComponent[] = [
      { types: ["street_number"], short_name: fields.addressLine1, long_name: fields.addressLine1 },
      { types: ["locality"], short_name: fields.city, long_name: fields.city },
      { types: ["administrative_area_level_1"], short_name: fields.state, long_name: fields.state },
      { types: ["postal_code"], short_name: formatPostalCode(fields.zip), long_name: formatPostalCode(fields.zip) },
      { types: ["country"], short_name: fields.country, long_name: fields.country },
      ...(fields.addressLine2 ? [{ types: ["subpremise"], short_name: fields.addressLine2, long_name: fields.addressLine2 }] : []),
    ];

    onChange({ value, isValid, components });
  };

  const onManualChange = (field: keyof typeof custom, val: string) => {
    hasManualEditsRef.current = true; // Mark that user has made manual edits
    const next = { ...custom, [field]: val };
    setCustom(next);
    buildAndNotify(next);
  };

  // Helper to merge suite value into Google address components
  const mergeSubpremiseIntoComponents = (components: GoogleAddressComponent[], suiteValue: string): GoogleAddressComponent[] => {
    // Remove any existing subpremise component
    const filtered = components.filter(c => !c.types.includes("subpremise"));
    // Add new subpremise if value is not empty
    if (suiteValue.trim()) {
      filtered.push({ types: ["subpremise"], short_name: suiteValue, long_name: suiteValue });
    }
    return filtered;
  };

  // Handler for suite field change in Google mode
  const onGoogleModeSuiteChange = (val: string) => {
    setGoogleModeSuite(val);
    if (storedGoogleAddress) {
      const updatedComps = mergeSubpremiseIntoComponents(storedGoogleAddress.components, val);
      // Update stored address with new components
      setStoredGoogleAddress({ ...storedGoogleAddress, components: updatedComps });
      onChange({ value: storedGoogleAddress.value, isValid: true, components: updatedComps });
    }
  };

  useEffect(() => {
    setUseManual(hideGooglePlaceSearch);
  }, [hideGooglePlaceSearch]);

  // Update stored Google address when initialValue or initialComponents change
  useEffect(() => {
    if (initialValue && initialComponents.length > 0 && !useManual) {
      setStoredGoogleAddress({ value: initialValue, components: initialComponents });
    }
  }, [initialValue, initialComponents, useManual]);

  useEffect(() => {
    // Skip if we're in the middle of a toggle to prevent infinite loops
    if (isTogglingRef.current) {
      return;
    }
    // Only update if we're in manual mode
    if (!useManual) {
      return;
    }
    // Don't overwrite manual edits that the user has made
    if (hasManualEditsRef.current) {
      return;
    }
    // Use deep comparison since initialData might be recreated on every render
    if (isInitialDataEqual(initialData, lastInitialDataRef.current)) {
      return;
    }
    lastInitialDataRef.current = initialData;
    const updated = buildInitialCustom(addressType);
    setCustom(updated);
    // Don't call buildAndNotify here - it causes infinite loops
    // The toggle handler and onManualChange handle parent notifications
  }, [initialData, addressType, useManual]);

  return (
    <div className="space-y-2">
      {/* Top label + toggle */}
      <div className="flex items-center justify-between">
        <span className="text-lg font-medium">
          {label}
          {required && <span className="text-sm text-red-500">*</span>}
        </span>
        <div className="flex gap-3 justify-end">
          <label className="inline-flex items-center cursor-pointer ml-9">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={useManual}
              disabled={disabled}
              onChange={() => {
                const next = !useManual;
                isTogglingRef.current = true;
                setUseManual(next);
                onModeChange?.(next);

                if (next) {
                  // Switching to manual mode
                  if (resetOnToggle) {
                    // Step1: always start with empty form
                    const emptyInitial = {
                      addressLine1: "",
                      addressLine2: "",
                      unit: "",
                      city: "",
                      state: "",
                      zip: "",
                      country: "CA",
                      formattedAddress: "",
                    };
                    setCustom(emptyInitial);
                    hasManualEditsRef.current = false;
                    buildAndNotify(emptyInitial);
                  } else {
                    // Other screens: preserve stored Google / initial data
                    let componentsToUse: GoogleAddressComponent[] | null = null;
                    if (storedGoogleAddress && storedGoogleAddress.components.length > 0) {
                      componentsToUse = storedGoogleAddress.components;
                    } else if (initialComponents && initialComponents.length > 0) {
                      componentsToUse = initialComponents;
                    }
                    const initial = buildInitialCustom(addressType, componentsToUse || undefined);
                    setCustom(initial);
                    hasManualEditsRef.current = true;
                    buildAndNotify(initial);
                  }
                } else {
                  // Switching to Google mode
                  hasManualEditsRef.current = false;
                  if (resetOnToggle) {
                    // Step1: always clear so user has to fill again
                    setStoredGoogleAddress(null);
                    setGoogleModeSuite("");
                    onChange({ value: "", isValid: false, components: [] });
                  } else {
                    // Other screens: restore stored Google or initial
                    if (storedGoogleAddress) {
                      // Extract suite from stored components
                      const extracted = extractAddressFromComponents(storedGoogleAddress.components);
                      setGoogleModeSuite(extracted.addressLine2 || "");
                      onChange({ value: storedGoogleAddress.value, isValid: true, components: storedGoogleAddress.components });
                    } else if (initialValue) {
                      // Extract suite from initial components
                      const extracted = extractAddressFromComponents(initialComponents);
                      setGoogleModeSuite(extracted.addressLine2 || "");
                      onChange({ value: initialValue, isValid: !!initialValue, components: initialComponents });
                    } else {
                      setGoogleModeSuite("");
                      onChange({ value: "", isValid: false, components: [] });
                    }
                  }
                }

                setTimeout(() => {
                  isTogglingRef.current = false;
                }, 100);
              }}
            />

            {/* track */}
            <div className="relative w-11 mt-1 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-orange-600 dark:peer-checked:bg-orange-600"></div>
            {/* thumb */}
            <span className="ms-3 text-sm font-medium text-black-900 dark:text-black-300">Type in your address</span>

            {/* label text */}
          </label>
        </div>
      </div>

      {useManual ? (
        <div className="space-y-4">
          {/* *** Street Address */}
          <div className="w-4/4">
            <TextInput
              placeholder="e.g., 123 Medical Plaza"
              disabled={disabled}
              isRequired={true}
              label={"Street Address"}
              isError={error && !custom.addressLine1}
              onChangeFunc={e => onManualChange("addressLine1", e.currentTarget.value)}
              value={custom?.addressLine1}
              isDarkBg={darkBg}
            />
            {error && !custom.addressLine1 ? <p className="text-base text-red-600">Please provide street address</p> : null}
          </div>
          <div className="w-4/4">
            <TextInput
              placeholder="e.g., Suite 100"
              disabled={disabled}
              isRequired={false}
              label={"Suite, Floor, or Unit"}
              isError={false}
              onChangeFunc={e => onManualChange("addressLine2", e.currentTarget.value)}
              value={custom?.addressLine2}
              isDarkBg={darkBg}
            />
          </div>
          {/* <div className="w-4/4">
            <TextInput
              placeholder="Unit"
              disabled={disabled}
              isRequired={false}
              label={"Unit"}
              isError={false}
              onChangeFunc={e => onManualChange("unit", e.currentTarget.value)}
              value={custom?.unit}
              isDarkBg={darkBg}
              isNumber={true}
            />
          </div> */}
          {/* *** City at 50% width */}
          <div className="flex flex-wrap -mx-2">
            <div className="w-1/2 px-2 mb-4">
              <TextInput
                placeholder="City"
                disabled={disabled}
                isRequired={true}
                label={"City"}
                isError={error && !custom.city}
                onChangeFunc={e => onManualChange("city", e.currentTarget.value)}
                value={custom.city}
                isDarkBg={darkBg}
              />
              {error && !custom.city ? <p className="text-base text-red-600">Please provide city address</p> : null}
            </div>
            {/* <div className="w-1/2 px-2 mb-4">
              <SelectNew
                key="state"
                value={ProvinceList.some(p => p.value === custom.state) ? custom.state : ""}
                onSelect={e => onManualChange("state", e.currentTarget.value)}
                placeHolder="Select a Province"
                errorMsg={""}
                title="State/Province"
                required
                disabled={disabled}
                options={ProvinceList.map(p => ({ value: p.value, label: p.text }))}
                readOnly={disabled}
                isDarkBg={darkBg}
              />
              {error && !custom.state ? <p className="text-base text-red-600">Please provide state address</p> : null}
            </div> */}
            <div className="w-1/2 px-2 mb-4">
              <TextInput
                placeholder="Postal Code"
                disabled={disabled}
                isRequired={true}
                label={"Postal Code"}
                isError={error && !custom.zip}
                onChangeFunc={e => onManualChange("zip", formatPostalCode(e.currentTarget.value))}
                value={formatPostalCode(custom.zip)}
                isDarkBg={darkBg}
                maxLength={7}
              />
              {error && !custom.zip ? <p className="text-base text-red-600">Please provide zip/postal code</p> : null}
            </div>
            {/* <div className="w-1/2 px-2 mb-4">
              <SelectNew
                key="country"
                value="CA"
                onSelect={() => {}}
                placeHolder=""
                errorMsg=""
                title="Country"
                required
                disabled={true}
                options={[{ value: "CA", label: "Canada" }]}
                isDarkBg={darkBg}
              />
            </div> */}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <GooglePlaceSearchComponent
            placeholder="Start typing address…"
            label=""
            defaultValue={initialValue}
            isRequired={required}
            isError={error}
            errorMsg={errorMsg}
            disabled={disabled}
            onChange={e => {
              onChange({ value: e.currentTarget.value, isValid: !!e.currentTarget.value, components: [] });
            }}
            onPlaceSelected={place => {
              const comps: GoogleAddressComponent[] = place.address_components;
              const built = place.formatted_address as string;

              // Extract subpremise (unit) from Google response to pre-fill suite field
              const extracted = extractAddressFromComponents(comps);
              const suiteFromGoogle = extracted.addressLine2 || "";
              setGoogleModeSuite(suiteFromGoogle);

              // Store the selected address so we can restore it when toggling back
              setStoredGoogleAddress({ value: built, components: comps });
              onChange({ value: built, isValid: true, components: comps });
            }}
          />
          <div className="w-full">
            <TextInput
              placeholder="e.g., Suite 100"
              disabled={disabled}
              isRequired={false}
              label={"Suite, Floor, or Unit"}
              isError={false}
              onChangeFunc={e => onGoogleModeSuiteChange(e.currentTarget.value)}
              value={googleModeSuite}
              isDarkBg={darkBg}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressField;
